import React, { useState } from 'react';
import defaultTo from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormSection, FormHeader, FormFooter, Field } from '@atlaskit/form';
import { ShipToPartyWithoutId } from '@atlassian/cssxp-ccp-types';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { createContactRelationship, deleteContactRelationship, updateInvoiceGroup } from '@atlassian/temp-proxies';
import { UpdateInvoiceGroupRequest, InvoiceGroupPurchaseOrder } from '@atlassian/cssxp-types-billing';
import { formatShipToAddress, InvoiceContactsStep, CurrencyPONumberStep } from '@css-xp/billing-v2';
import { AddContactFields, AddContactSelectFields, AvailableOptionsProp } from '@css-xp/ppc-add-contact-fields';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  successNotificationBuilder,
  errorNotificationBuilder,
  ErrorComponents,
  InflightInvoiceBanner,
  NotificationTypes,
  useFeatureToggle,
  ContactsTooltip,
  ContactsTooltipType,
} from '@css-xp/base';
import { withApollo } from '@css-xp/base/graphql-provider';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { HelpContent, failureMessage } from '@css-xp/ppc-common';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { COMMERCIAL_PRICE_ELIGIBILITY, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import {
  CreateContactRelationshipsRequest,
  DeleteContactRelationshipsRequest,
} from '@css-xp/contact-resources/src/constant';
import {
  useContactServicePayload,
  useFetchEccFromContactService,
  useFetchBillingContactsFromContactService,
} from '@css-xp/contact-resources';
import { EndCustomerContact } from '@css-xp/graphtypes/src/generated-types-new';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { SoldToAddressSection } from '../sold-to-address-section';
import { fetchTransactionAccountForEditBillingProfilePane } from '../../../queries/fetch-transaction-account-for-edit-billing-profile-pane';
import { CustomerContacts } from './edit-billing-profile-pane.styles';
import { getMaxLimitOfCustomerContact } from '@css-xp/ppc-utils';

interface FormData {
  recipients: string[];
  invoiceGroupName: string;
  purchaseOrderNumber?: string | null;
}

interface NewShipToAddress {
  shipToPartyId?: string | null;
  shipToParty?: ShipToPartyWithoutId | null | undefined;
}

interface Props {
  recipients?: string[];
  id: string;
  shipToParty?: generatedTypesNew.CommerceAddress;

  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId: string;
  name?: string;
  purchaseOrder?: InvoiceGroupPurchaseOrder | null;
  onSubmit?: () => Promise<void>;
  inflightBannerMessage?: string;
  validDomains?: string[] | null | undefined;
  txaBillingAdmins?: AvailableOptionsProp[];
  billingAdminsLoading?: boolean;
  isBillingProfileUsedForInternalUsageLicense: boolean;
}

const notificationOptions = successNotificationBuilder()
  .withId('update-billing-profile-success')
  .withHeader('Update successful')
  .withMessage('Billing profile updated')
  .withDismissAfter(5000)
  .isVersion(NotificationTypes.FLAG)
  .build();

const errorNotificationOptions = errorNotificationBuilder()
  .withId('ecc-not-updated')
  .withHeader("We're unable to update customer contact")
  .withMessage(failureMessage())
  .withDismissAfter(5000)
  .isVersion(NotificationTypes.FLAG)
  .build();

export const EditBillingProfilePane = withApollo(
  ({
    recipients,
    id,
    refetch,
    shipToParty,
    transactionAccountId,
    name = '',
    purchaseOrder,
    onSubmit,
    inflightBannerMessage = 'All changes will be reflected to the orders included in the inflight invoice',
    validDomains,
    txaBillingAdmins,
    billingAdminsLoading,
    isBillingProfileUsedForInternalUsageLicense,
  }: Props): JSX.Element => {
    const [adminFieldsDirty, setAdminsDirtyFlag] = useState<boolean>(true);
    const [existingCustomerContacts, setExistingCustomerContacts] = useState<EndCustomerContact[]>([]);
    const [existingBillingAdmins, setExistingBillingAdmins] = useState<EndCustomerContact[]>([]);

    const [isEnabled] = useFeatureToggle();
    const { getECCDetails, eccDetailsLoading } = useFetchEccFromContactService();
    const { getBillingContactDetails, billingContactsLoading } = useFetchBillingContactsFromContactService();

    const { getCreateContactRelationshipsPayload, getDeleteContactRelationshipsPayload } = useContactServicePayload();

    const isBillingProfileNotificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NOTIFICATION_AT_BILLING_PROFILE);
    const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
    const isNonCommercialPricingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING);
    const isCustomerContactV3Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);

    React.useEffect(() => {
      // fetch customer contacts for invoice group
      (async () => {
        if (!isCustomerContactV3Enabled) {
          const data = await getECCDetails(id, transactionAccountId);
          setExistingCustomerContacts(data);
        }
      })();
      // fetch billing contact for invoice group
      (async () => {
        if (!isCustomerContactV3Enabled && isBillingProfileNotificationEnabled) {
          const data = await getBillingContactDetails(id, transactionAccountId);
          setExistingBillingAdmins(data);
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { data: transactionAccountData, loading, error } = useQuery<
      generatedTypesNew.FetchTransactionAccountForEditBillingProfilePaneQuery,
      generatedTypesNew.FetchTransactionAccountForEditBillingProfilePaneQueryVariables
    >(fetchTransactionAccountForEditBillingProfilePane, {
      variables: { id: transactionAccountId },
      fetchPolicy: 'no-cache',
    });

    const isPOMultipleUseChecked = purchaseOrder ? !purchaseOrder.oneTimeUse : false;

    const [newShipToAddress, setNewShipToAddress] = React.useState<
      generatedTypesNew.CommerceAddress | undefined | null
    >(shipToParty);

    const invoiceRecipients = !isNil(recipients) && recipients?.length === 0 ? [''] : recipients;

    const { closeSidebar } = useSidebar();

    const [atlassianAccountIds, setAtlassianAccountIds] = useState(['']);
    const [atlassianAccountEmails, setAtlassianAccountEmails] = useState<Array<string>>(['']);
    const [billingAdminAtlassianAccountIds, setBillingAdminAtlassianAccountIds] = useState(['']);
    const [billingContactAtlassianAccountEmails, setBillingContactAtlassianAccountEmails] = useState<Array<string>>([
      '',
    ]);

    React.useEffect(() => {
      const accountIds = existingCustomerContacts?.map(({ accountId }) => accountId);
      const accountEmails = existingCustomerContacts?.map(({ email }) => email || name);
      const existingAccountIds = accountIds && accountIds.length > 0 ? accountIds : [''];
      const existingAccountEmails = accountEmails && accountEmails.length > 0 ? accountEmails : [''];
      setAtlassianAccountIds(existingAccountIds);
      setAtlassianAccountEmails(existingAccountEmails);
    }, [existingCustomerContacts]);

    React.useEffect(() => {
      const accountIds = existingBillingAdmins?.map(({ accountId }) => accountId);
      const accountEmails = existingBillingAdmins?.map(({ email }) => email || name);
      const existingAccountIds = accountIds && accountIds.length > 0 ? accountIds : [''];
      const existingAccountEmails = accountEmails && accountEmails.length > 0 ? accountEmails : [''];
      setBillingAdminAtlassianAccountIds(existingAccountIds);
      setBillingContactAtlassianAccountEmails(existingAccountEmails);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingBillingAdmins]);

    const oldShipTo = {
      shipToParty: {
        name: shipToParty?.name,
        postalAddress: {
          line1: shipToParty?.addressLine1,
          line2: shipToParty?.addressLine2,
          city: shipToParty?.city,
          country: shipToParty?.country,
          state: shipToParty?.state,
          postcode: shipToParty?.postcode,
        },
        taxId: shipToParty?.taxId || null,
        taxIds: shipToParty?.taxIds?.length ? shipToParty?.taxIds : null,
      },
    };

    const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateInvoiceGroupRequest, any>({
      submitFormAction: onSubmit ? onSubmit : updateInvoiceGroup,
    });

    const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();

    const updateAdminFieldsDirtyFlag = (flag: boolean) => {
      setAdminsDirtyFlag(flag);
    };

    const handleFormSubmit = (formData: FormData) => {
      let newShipTo: NewShipToAddress = {
        shipToParty: {
          name: newShipToAddress?.name || '',
          postalAddress: {
            line1: newShipToAddress?.addressLine1 || '',
            line2: newShipToAddress?.addressLine2 || '',
            city: newShipToAddress?.city || '',
            country: newShipToAddress?.country || '',
            state: newShipToAddress?.state || '',
            postcode: newShipToAddress?.postcode || '',
          },
          taxId: newShipToAddress?.taxId || null,
          taxIds: newShipToAddress?.taxIds?.length ? (newShipToAddress?.taxIds as generatedTypesNew.TaxId[]) : null,
        },
      };

      // compare the two shipTo's if equal or if no address changed keep the old shipToId
      if (
        isEqual(formatShipToAddress(oldShipTo.shipToParty), formatShipToAddress(newShipTo.shipToParty)) ||
        !newShipToAddress
      ) {
        // shipToAddress didn't change. Don't send it in the request
        newShipTo = { shipToPartyId: shipToParty?.id ?? null };
      } else if (newShipToAddress && !isEmpty(newShipToAddress)) {
        newShipTo = { shipToPartyId: newShipToAddress.id };
      }

      const requestData: UpdateInvoiceGroupRequest = {
        data: {
          ...newShipTo,
          name: formData?.invoiceGroupName,
        },
        invoiceGroupId: id,
        transactionAccountId: transactionAccountId,
      };

      const newRecipients = formData?.recipients?.filter((recipient: string) => recipient.length > 0) || [];
      const existingRecipients = invoiceRecipients?.filter((recipient: string) => recipient.length > 0) || [];

      if (
        !isCustomerContactV3Enabled &&
        (newRecipients.length < existingRecipients.length ||
          newRecipients.some((recipient) => !existingRecipients.includes(recipient)))
      ) {
        requestData.data.recipients = newRecipients;
      }

      //to update customer contacts of billing profile
      !isCustomerContactV3Enabled && contactServiceECCHandler();

      submitForm(requestData);
    };

    const contactServiceECCHandler = async () => {
      try {
        // Customer Contacts to be added
        const existingAccountIds = existingCustomerContacts?.map((contact) => contact.accountId);
        const createCustomerContactIds = atlassianAccountIds?.filter((id) => !existingAccountIds?.includes(id));
        // Billing Contacts (Billing admins at invoice group level) to be added
        const existingBillingAdminIds = existingBillingAdmins?.map((contact) => contact.accountId);
        const createBillingContactIds = billingAdminAtlassianAccountIds?.filter(
          (id) => id && !existingBillingAdminIds?.includes(id)
        );
        const deleteCustomerContactIds = existingAccountIds?.filter((id) => !atlassianAccountIds.includes(id));
        const deleteBillingContactIds = existingBillingAdminIds?.filter(
          (id) => id && !billingAdminAtlassianAccountIds.includes(id)
        );
        // ContactType.
        const createCustomerContactPayload = getCreateContactRelationshipsPayload({
          invoiceGroupId: id,
          transactionAccountId: transactionAccountId,
          createContactIds: createCustomerContactIds,
          createBillingContactIds: isBillingProfileNotificationEnabled ? createBillingContactIds : [],
        });

        const deleteCustomerContactPayload = getDeleteContactRelationshipsPayload({
          invoiceGroupId: id,
          transactionAccountId: transactionAccountId,
          deleteContactIds: deleteCustomerContactIds,
          deleteBillingContactIds: isBillingProfileNotificationEnabled ? deleteBillingContactIds : [],
        });

        //create contacts if required
        if (!isEmpty(createCustomerContactPayload)) {
          await createContactRelationship(createCustomerContactPayload as CreateContactRelationshipsRequest);
        }

        // delete customer contacts if required
        if (!isEmpty(deleteCustomerContactPayload)) {
          await deleteContactRelationship(deleteCustomerContactPayload as DeleteContactRelationshipsRequest);
        }
      } catch (err) {
        if (!notificationSent) {
          sendErrorNotification(errorNotificationOptions);
          setNotificationSent(true);
          closeSidebar();
          refetch();
        }
      }
    };

    if (complete && !notificationSent) {
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
      closeSidebar();
      refetch();
    }

    const purchaseOrderNumber = purchaseOrder && purchaseOrder.number ? purchaseOrder.number : '';

    if (loading) return <LoadingIndicator />;
    if (error) {
      const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
      return <GenericError error={error} />;
    }

    return (
      <div className="billing-profile pane-sidebar-content" data-testid="billing-profile-sidebar">
        <Form onSubmit={handleFormSubmit}>
          {({ formProps, dirty }) => (
            <form data-testid="edit-billing-profile-form" {...formProps}>
              <FormHeader title="Edit billing profile"></FormHeader>
              <InflightInvoiceBanner transactionAccountId={transactionAccountId} message={inflightBannerMessage} />
              <FormSection>
                {formError ? (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to update the billing profile for the following reason"
                        message={formError}
                        helpContent={<HelpContent />}
                      />
                    }
                  />
                ) : !transactionAccountId ? (
                  <InfoPanel
                    type="warning"
                    title={'Cannot update billing profile'}
                    message={'This billing profile is missing transaction-account id'}
                  />
                ) : null}
              </FormSection>
              <SoldToAddressSection
                transactionAccountData={transactionAccountData}
                shipToAddress={shipToParty}
                onShipToAddressUpdate={setNewShipToAddress}
                billingProfileData={{
                  invoiceGroupName: name,
                }}
                isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
                isNewSoldToAddressDisabled={
                  isNonCommercialPricingEnabled &&
                  shipToParty?.shipToPriceEligibility &&
                  shipToParty?.shipToPriceEligibility?.toLowerCase() !== COMMERCIAL_PRICE_ELIGIBILITY.toLowerCase()
                }
                isBillingProfileUsedForInternalUsageLicense={isBillingProfileUsedForInternalUsageLicense}
              />
              {!isCustomerContactV3Enabled && (
                <FormSection>
                  <CustomerContacts>
                    <ContactsTooltip type={ContactsTooltipType.CUSTOMER}>
                      <h3>Customer contacts</h3>
                    </ContactsTooltip>
                    <Field name="customer-contact">
                      {() =>
                        eccDetailsLoading ? (
                          <SkeletonLoaderWrapper testId="order-review-ecc-loading">
                            <SkeletonLoader height="40px" />
                          </SkeletonLoaderWrapper>
                        ) : (
                          <AddContactFields
                            setAtlassianAccountIds={setAtlassianAccountIds}
                            atlassianAccountIds={atlassianAccountIds}
                            atlassianAccountEmails={atlassianAccountEmails}
                            setAtlassianAccountEmails={setAtlassianAccountEmails}
                            currentUserList={atlassianAccountIds}
                            userAlreadyExistText="Customer contact already exists"
                            emailFieldText="Enter contact email"
                            nameFieldText="Enter contact full name"
                            addButtonText="Add customer contact"
                            isAddNewContactDisabled={atlassianAccountIds.length >= getMaxLimitOfCustomerContact()}
                            isExistingContactRemoveDisabled={true}
                          />
                        )
                      }
                    </Field>
                  </CustomerContacts>
                </FormSection>
              )}
              {!isCustomerContactV3Enabled && isBillingProfileNotificationEnabled && (
                <FormSection>
                  <CustomerContacts>
                    <ContactsTooltip type={ContactsTooltipType.BILLING_CONTACT}>
                      <h3>Billing admins</h3>
                    </ContactsTooltip>
                    <Field name="customer-contact">
                      {() =>
                        billingContactsLoading || billingAdminsLoading ? (
                          <SkeletonLoaderWrapper testId="order-review-ecc-loading">
                            <SkeletonLoader height="40px" />
                          </SkeletonLoaderWrapper>
                        ) : (
                          <AddContactSelectFields
                            setAtlassianAccountIds={setBillingAdminAtlassianAccountIds}
                            atlassianAccountIds={billingAdminAtlassianAccountIds}
                            atlassianAccountEmails={billingContactAtlassianAccountEmails}
                            setAtlassianAccountEmails={setBillingContactAtlassianAccountEmails}
                            userAlreadyExistText="Billing admin already exists"
                            emailFieldText="Select billing admin"
                            addButtonText="Add billing admin"
                            isExistingContactRemoveDisabled={false}
                            fieldType="billing-contact"
                            availableOptions={txaBillingAdmins}
                            availableOptionsLoading={billingAdminsLoading}
                          />
                        )
                      }
                    </Field>
                  </CustomerContacts>
                </FormSection>
              )}
              {!isCustomerContactV3Enabled && (
                <InvoiceContactsStep
                  onUpdateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                  defaultRecipients={invoiceRecipients ?? ['']}
                  validDomains={validDomains}
                />
              )}
              <CurrencyPONumberStep
                isPONumberFeatureEnabled={false}
                isPOMultipleUseChecked={isPOMultipleUseChecked}
                purchaseOrderNumber={defaultTo(purchaseOrderNumber, '')}
                hideCurrency={true}
              />
              <FormFooter align="end">
                <ButtonGroup>
                  <Button appearance="default" onClick={closeSidebar}>
                    Cancel
                  </Button>
                  <LoadingButton
                    isDisabled={!dirty && !adminFieldsDirty}
                    appearance="primary"
                    type="submit"
                    testId="update-button"
                    isLoading={isProcessing}
                  >
                    Save
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            </form>
          )}
        </Form>
      </div>
    );
  }
);

export default React.memo(EditBillingProfilePane);
